import { ParagraphSnapshot } from '../../../core/types/Typesetter';
import { calculateNewSnapshotCache, mergeSnapshotsByParagraphId, EditDeltaInfo } from './SnapshotCacheUpdater';
import { WasmLogger } from '../../../../../utils/loggers/WasmLogger';

// Debug flag - set to false for production
const DEBUG_ENABLED = false;

/**
 * SnapshotCacheManager - Utility for managing paragraph snapshot cache updates
 *
 * Handles the logic for updating cached paragraph snapshots during incremental
 * layout operations. This includes calculating new cache state and applying
 * updates with appropriate logging.
 */
export class SnapshotCacheManager {
  /**
   * Update cached paragraph snapshots with new snapshots
   *
   * This method calculates the new snapshot cache using the provided snapshots
   * and updates the cached snapshot array if changes are detected. Used during
   * incremental layout operations to keep the paragraph cache in sync.
   *
   * @param cachedParagraphSnapshots - Current cached paragraph snapshots (will be updated)
   * @param newSnapshots - Array of new ParagraphSnapshot from incremental layout
   * @param oldCacheForMerge - Optional old cache to use for merging partial snapshots
   * @param editDelta - Optional edit delta for adjusting grapheme indices of preserved lines
   * @param boundariesChanged - Whether paragraph boundaries changed (split/merge via \n\n)
   * @param affectedParagraphIds - IDs of affected paragraphs from WASM
   * @param deletedParagraphIds - IDs of paragraphs that were deleted/merged by WASM
   * @param allParagraphIds - All paragraph IDs in the document (from WASM) for definitive validation
   */
  static updateCachedSnapshots(
    cachedParagraphSnapshots: ParagraphSnapshot[],
    newSnapshots: ParagraphSnapshot[],
    oldCacheForMerge?: ParagraphSnapshot[],
    editDelta?: EditDeltaInfo,
    boundariesChanged?: boolean,
    affectedParagraphIds?: string[],
    deletedParagraphIds?: string[],
    allParagraphIds?: string[]
  ): void {
    // 🔥 CRITICAL FIX: Use oldCacheForMerge if provided (for selection delete merging)
    // When the doc hash changes, the main cache is cleared but we save a copy for merging.
    // This allows partial snapshots from WASM to be merged with the old full snapshot.
    const cacheForMerge = oldCacheForMerge && oldCacheForMerge.length > 0 
      ? oldCacheForMerge 
      : cachedParagraphSnapshots;
    
    let newCache: ParagraphSnapshot[];
    
    // 🔥 DELETE FIX V2 (2026-01-12): For DELETE operations, use smart merge that:
    // 1. REPLACES affected paragraphs with WASM output (via affectedParagraphIds check)
    // 2. PRESERVES + SHIFTS unaffected paragraphs that come AFTER the edit
    // 
    // **WHY SIMPLE REPLACE FAILS:**
    // WASM only returns AFFECTED paragraphs, not the entire document.
    // If we replace the entire cache with just WASM output, we LOSE paragraphs
    // that weren't affected (e.g., "test" at the end disappears).
    //
    // **WHY NAIVE MERGE FAILED (duplicates):**
    // The merge was preserving+shifting paragraphs that WASM already handled,
    // causing them to collide with new WASM output → DUPLICATE_LINE_RANGES.
    //
    // **THE FIX:**
    // The merge function has an `affectedParagraphIds?.includes(paraKey)` check
    // that skips preserving paragraphs that WASM handled. This ensures:
    // - Affected paragraphs → WASM output wins (no duplicates)
    // - Unaffected paragraphs → Preserve + shift indices
    //
    // **SCALING (1 page to 500 pages):**
    // - WASM returns affected paragraphs (not entire document)
    // - Unaffected paragraphs are preserved+shifted locally
    // - This is efficient because most of the document doesn't change
    //
    const isDeleteOperation = editDelta && editDelta.deleteCount > 0 && editDelta.insertLength === 0;
    
    // 🔥 FIX (2026-01-13): ALWAYS use multi-paragraph merge.
    // 
    // WHY: calculateNewSnapshotCache does LINE-LEVEL merging which pollutes WASM's
    // complete output with stale cache data, causing DUPLICATE_LINE_RANGES errors.
    // 
    // WASM returns COMPLETE ParagraphSnapshots for affected paragraphs:
    // - All lines (from break_into_lines)
    // - Complete caret_map (from build_caret_map covering all lines)
    // 
    // The merge should:
    // - REPLACE affected paragraphs entirely with WASM output (verbatim)
    // - PRESERVE unaffected paragraphs with shifted indices
    // - DELETE paragraphs in deletedParagraphIds
    //
    // mergeSnapshotsByParagraphId already does this correctly via the
    // affectedParagraphIds check. calculateNewSnapshotCache does NOT.
    WasmLogger.snapshot.merge('Using PARAGRAPH-LEVEL merge (WASM = source of truth)', {
      isDeleteOperation,
      boundariesChanged,
      newSnapshotsCount: newSnapshots.length,
      cacheForMergeCount: cacheForMerge.length,
      affectedParagraphIds,
      deletedParagraphIds,
      allParagraphIdsCount: allParagraphIds?.length ?? 'N/A'
    });
    newCache = mergeSnapshotsByParagraphId(cacheForMerge, newSnapshots, editDelta, affectedParagraphIds, deletedParagraphIds, allParagraphIds);

    // Update cache if it changed
    if (newSnapshots.length > 0) {
      cachedParagraphSnapshots.length = 0; // Clear the array
      cachedParagraphSnapshots.push(...newCache); // Add new snapshots
      DEBUG_ENABLED && WasmLogger.snapshot.merge(`Replaced snapshot cache with ${newCache.length} merged snapshots (from ${newSnapshots.length} new + ${cacheForMerge.length} old)`, {
        editDelta,
        mergedLinesCount: newCache[0]?.lines?.length ?? 0
      });
    }

    DEBUG_ENABLED && WasmLogger.snapshot.merge(`Updated snapshot cache: ${cachedParagraphSnapshots.length} total paragraphs`);
  }
}
